/**
 * MUITableRequest
 *
 * Variant of MUITable tailored for Request dashboards. Adds support for row expansion
 * and request-specific defaults (e.g., default status filter to Pending).
 *
 * Differences from MUITable
 * - Accepts `expandedRow` to style/identify the currently expanded row.
 * - Accepts `isShowMessageLoading` to optionally control message loading state (prop only).
 * - Shares filtering, chat popover, and actions menu behavior with base table.
 *
 * Props: See MuiTableProps plus `expandedRow` and `isShowMessageLoading` usage in parent.
 */
// src/components/MUITableTable/MUITableTable.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "./mui-table-request.scss";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { MuiTableProps } from "./mui-table-props";
import TableHeaderArrow from "../../../assets/icons/sortIcon.svg";
import ChatIcon from "../../../assets/icons/chatIcon.svg";
import ActionsIcon from "../../../assets/icons/actionsIcon.svg";
import FilterIcon from "../../../assets/icons/filter-White.svg";
import NoRecordsComponent from "../no-records-component/no-records-component";
import ChatPopover from "../chat-popover/chat-popover";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import { Popover, Tooltip } from "@mui/material";
import infoIcon from "../../../assets/icons/info-icon.svg";

export interface TableHeader {
  key: string;
  label: string;
  filterable?: boolean;
}
interface FilterOption {
  key: number | string;
  value: string;
}

interface Filters {
  [columnKey: string]: FilterOption[];
}
// 👇 Define sortable column keys here - if a column key matches, it will show sort icon
const SORTABLE_COLUMNS = new Set([
  "requestType",
  "title",
  "linkedTo",
  "approver",
  "date",
  "requester",
  "status",
]);

  const TextOverflow: React.FC<{ value: string }> = ({ value }) => {
    const spanRef = useRef<HTMLSpanElement | null>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    const checkOverflow = () => {
      const el = spanRef.current;
      if (!el) return;
      // Compare scroll vs client width to detect truncation
      const overflowed = el.scrollWidth > el.clientWidth + 1; // +1 for subpixel safety
      setIsOverflow(overflowed);
    };

    useEffect(() => {
      checkOverflow();
      // Also re-check on globalThis.resize
      const onResize = () => checkOverflow();
      globalThis.addEventListener("resize", onResize);
      return () => globalThis.removeEventListener("resize", onResize);
    }, [value]);

    return (
      <Tooltip
        title={value}
        placement="bottom-start"
        disableHoverListener={!isOverflow}
      >
        <button
          style={{
            border: "none",
            background: "none",
            padding: 0,
            margin: 0,
            cursor: "default",
          }}
          onMouseEnter={checkOverflow}
          >
          <span
            ref={spanRef}
            className="cell-text-ellipsis"
          >
            {value}
          </span>
        </button>
      </Tooltip>
    );
  };
  
// 👇 Define center-aligned column keys here
const MUITableRequest: React.FC<MuiTableProps> = ({
  columnNames,
  rowsData = [],
  showChatColumn,
  showActionsColumn,
  showActionsColumnWithOptions,
  onRowClick,
  menuOptions = [],
  selectedMenuOption,
  setSelectedMenuOption,
  isRequest = false,
  onFilterChange,
  onOpenChat,
  commentsByRow,
  activeChatRow,
  onCommentSubmit,
  departmentsList = [], // Add departmentsList prop with default empty array
  canWrite,
  showInfoColumn,
  expandedRow,
  noRecordsTitle,
  noRecordsMessage,
  isShowMessageLoading,
}) => {// NOSONAR
  // Renders cell text with a title only when it's visually truncated

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const [chatAnchorEl, setChatAnchorEl] = useState<HTMLElement | null>(null);
  // 👇 Sort menu state
  const [sortMenuAnchor, setSortMenuAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [sortMenuColumn, setSortMenuColumn] = useState<string>("");

  // Open sort menu
  const handleOpenSortMenu = (
    event: React.MouseEvent<HTMLElement>,
    columnKey: string,
  ) => {
    event.stopPropagation();
    setSortMenuAnchor(event.currentTarget);
    setSortMenuColumn(columnKey);
  };

  // Close sort menu
  const handleCloseSortMenu = () => {
    setSortMenuAnchor(null);
    setSortMenuColumn("");
  };

  // Handle sort selection from menu
  const handleSortSelection = (direction: "asc" | "desc" | null) => {
    if (direction === null) {
      // Clear sorting
      setSortConfig(null);
    } else {
      setSortConfig({ key: sortMenuColumn, direction });
    }
    handleCloseSortMenu();
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedMenuOption(row?.id);
    setSelectedRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedMenuOption(null);
    setSelectedRow(null);
  };
  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (filterRef?.current?.contains(event.target) === false) {
        setOpenFilter(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  const openMenu = Boolean(anchorEl);

  const ITEM_HEIGHT = 48;
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [selectedFilters, setSelectedFilters] = useState<{
    [key: string]: FilterOption[];
  }>(() => ({
    ...(isRequest ? { status: [{ key: 2, value: "Pending" }] } : {}),
    department: [], // ✅ initialize department filter as empty array
  }));

  // Ensure parent knows about default filter on initial render (e.g., Pending for requests)
  useEffect(() => {
    if (!onFilterChange) return;
    if (isRequest) {
      onFilterChange({ status: ["pending"] });
    }
  }, []);

  const handleFilterSelect = (columnKey: string, option: FilterOption) => {
    setSelectedFilters((prev) => {
      const alreadySelected = prev[columnKey]?.some(
        (f) => f.key === option.key,
      );

      const updated = {
        ...prev,
        [columnKey]: alreadySelected
          ? prev[columnKey].filter((f) => f.key !== option.key)
          : [...(prev[columnKey] || []), option],
      };

      // ✅ Notify parent about filter change
      if (onFilterChange) {
        if (columnKey === "status") {
          const statuses = (updated.status || []).map((f) =>
            f.value.toLowerCase(),
          );
          // When nothing selected, send undefined so parent can show all
          onFilterChange({ status: statuses.length ? statuses : undefined });
        }
        if (columnKey === "department") {
          const departments = updated.department.map((f) => f.value);
          onFilterChange({ department: departments });
        }
      }

      return updated;
    });
  };

  const filters: Filters = {
    status: [
      { key: 1, value: "Approved" },
      { key: 2, value: "Pending" },
      { key: 3, value: "Rejected" },
    ],
    department: departmentsList,
  };
  const filteredRows = isRequest
    ? rowsData
    : rowsData.filter((row) => {
        return Object.entries(selectedFilters).every(([key, selected]) => {
          if (!selected || selected.length === 0) return true;

          const selectedValues = new Set(selected.map((f) => f.value));

          if (key === "status") {
            const cell = row[key] as
              | React.ReactElement<{ status?: string }>
              | string;
            if (React.isValidElement(cell)) {
              return selectedValues.has(cell.props.status ?? "");
            }
            return selectedValues.has(cell);
          }

          if (key === "department") {
            const cell = row[key];
            if (React.isValidElement(cell)) {
              const props = cell.props as any;
              const deptValue =
                props.departmentName || props.department || props.name;
              const matches = selectedValues.has(deptValue);
              return matches;
            } else {
              const matches = selectedValues.has(cell);
              return matches;
            }
          }

          // Default case for plain fields
          return selectedValues.has(row[key]);
        });
      });
  // ✅ Sorting state - 3-state cycle: asc → desc → reset
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: "asc" | "desc";
  } | null>(null);

  // ✅ compute sorted data after filtering
  const sortedRows = useMemo(() => {
    if (!sortConfig) return filteredRows;

    const { key, direction } = sortConfig;
    return [...filteredRows].sort((a, b) => {// NOSONAR
      let aVal = a[key];
      let bVal = b[key];

      // Helper function to deeply extract value from React elements
      const extractValue = (element: any): any => {// NOSONAR
        if (!React.isValidElement(element)) {
          return element;
        }

        const props: any = element.props;

        // Try to get value from common prop names
        let value =
          props.children ||
          props.value ||
          props.status ||
          props.label ||
          props.title ||
          props.text ||
          props.name ||
          "";

        // If children is also a React element, extract recursively
        if (React.isValidElement(value)) {
          return extractValue(value);
        }

        // If children is an array, try to extract from first non-element
        if (Array.isArray(value)) {
          for (const child of value) {
            if (!React.isValidElement(child) && child != null && child !== "") {
              return child;
            }
            if (React.isValidElement(child)) {
              const extracted = extractValue(child);
              if (extracted != null && extracted !== "") {
                return extracted;
              }
            }
          }
        }

        return value;
      };

      // Extract actual values from React elements
      aVal = extractValue(aVal);
      bVal = extractValue(bVal);

      // handle nulls / undefined gracefully
      if (aVal == null || aVal === "") return 1;
      if (bVal == null || bVal === "") return -1;

      // Convert to string for comparison
      const aStr =
        typeof aVal === "string" || typeof aVal === "number"
          ? aVal.toString().toLowerCase()
          : "";

      const bStr =
        typeof bVal === "string" || typeof bVal === "number"
          ? bVal.toString().toLowerCase()
          : "";

      if (aStr < bStr) return direction === "asc" ? -1 : 1;
      if (aStr > bStr) return direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredRows, sortConfig]);

  return (
    <div className="mui-table-request">
      <OverlayScrollbarsComponent
        className="table-scroll-wrapper-request"
        options={{
          overflow: { x: "scroll", y: "scroll" },
          scrollbars: {
            theme: "os-theme-dark",
            autoHide: "scroll",
            autoHideDelay: 400,
          },
        }}
      >
        <table>
          <thead>
            <tr>
              {columnNames.map((header) => {
                const isSortable = SORTABLE_COLUMNS.has(header.key);

                return (
                  <th key={header.key}>
                    <div
                      className="header-column"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        width: "100%",
                      }}
                    >
                      <span>{header.label}</span>

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                        }}
                      >
                        {header?.filterable &&
                          (filters[header.key]?.length ?? 0) > 0 && (
                            <button
                              style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                margin: 0,
                                cursor: "pointer",
                              }}
                              onClick={(e) => {
                                e.stopPropagation();
                                if ((filters[header.key]?.length ?? 0) === 0) {
                                  return;
                                }
                                setOpenFilter(
                                  openFilter === header.key ? null : header.key,
                                );
                              }}
                            >
                              <span
                                className="filter-pill"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  cursor: "pointer",
                                }}
                                aria-label={`Filter ${header.label}`}
                              >
                                <img
                                  src={FilterIcon}
                                  alt="Filter"
                                  className="filter-pill-icon"
                                />
                                {selectedFilters[header.key]?.length > 0 ? (
                                  <span className="filter-count">
                                    {selectedFilters[header.key].length}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          )}

                        {isSortable && (
                          <button
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              margin: 0,
                              cursor: "pointer",
                            }}
                            onClick={(e) => handleOpenSortMenu(e, header.key)}
                          >
                            <img
                              src={TableHeaderArrow}
                              alt="Sort"
                              style={{
                                cursor: "pointer",
                                width: "12px",
                                height: "12px",
                                opacity: sortConfig?.key === header.key ? 1 : 0.4,
                                transition: "opacity 0.2s ease",
                              }}
                              title="Sort options"
                            />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* ✅ filter dropdown stays untouched */}
                    {openFilter === header.key &&
                      filters[header.key]?.length > 0 && (
                        <div className="filter-dropdown" ref={filterRef}>
                          {filters[header.key]?.map((opt) => {
                            const isSelected = selectedFilters[
                              header.key
                            ]?.some((f) => f.key === opt.key);
                            return (
                              <button
                                style={{
                                  border: "none",
                                  background: "none",
                                  padding: 0,
                                  margin: 0,
                                  cursor: "pointer",
                                }}
                                onClick={() =>
                                  handleFilterSelect(header.key, opt)
                                }
                                key={opt.key}
                              >
                                <div
                                  key={opt.key}
                                  className={`filter-option ${isSelected ? "selected" : ""}`}
                                >
                                  <span>{opt.value}</span>
                                  <input
                                    type="checkbox"
                                    readOnly
                                    checked={isSelected}
                                  />
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}
                  </th>
                );
              })}

              {showChatColumn && <th></th>}
              {showInfoColumn && <th></th>}
              {showActionsColumn && <th></th>}
              {showActionsColumnWithOptions && <th></th>}
            </tr>
          </thead>

          {/* ✅ Sort Menu Popover */}
          <Popover
            open={Boolean(sortMenuAnchor)}
            anchorEl={sortMenuAnchor}
            onClose={handleCloseSortMenu}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
          >
            <div
              style={{
                padding: "4px 0 8px",
                minWidth: "150px",
              }}
            >
              {/* Ascending */}
              <button
                style={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  margin: 0,
                  cursor: "pointer",
                }}
                onClick={() => handleSortSelection("asc")}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) => {
                  if (
                    !(
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "asc"
                    )
                  ) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <div
                  style={{
                    marginBottom: "5px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor:
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "asc"
                        ? "#f0f0f0"
                        : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    checked={
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "asc"
                    }
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "13px" }}>Ascending</span>
                </div>
              </button>
              {/* Descending */}
              <button
                style={{
                  border: "none",
                  background: "none",
                  padding: 0,
                  margin: 0,
                  cursor: "pointer",
                }}
                onClick={() => handleSortSelection("desc")}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f5f5f5")
                }
                onMouseLeave={(e) => {
                  if (
                    !(
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "desc"
                    )
                  ) {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <div 
                  style={{
                    marginBottom: "5px",
                    padding: "8px 16px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    backgroundColor:
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "desc"
                        ? "#f0f0f0"
                        : "transparent",
                  }}
                >
                  <input
                    type="radio"
                    checked={
                      sortConfig?.key === sortMenuColumn &&
                      sortConfig.direction === "desc"
                    }
                    readOnly
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ fontSize: "13px" }}>Descending</span>
                </div>
              </button>

              {/* Clear Sort */}
              {sortConfig?.key === sortMenuColumn && (
                <>
                  <div
                    style={{
                      marginBottom: "5px",

                      height: "1px",
                      backgroundColor: "#e0e0e0",
                      margin: "4px 0",
                    }}
                  />
                  <button
                    style={{
                      border: "none",
                      background: "none",
                      padding: 0,
                      margin: 0,
                      cursor: "pointer",
                    }}
                    onClick={() => handleSortSelection(null)}
                  >
                    <div
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "4px 16px 0px",
                      }}
                    >
                      <span
                        style={{
                          color: "#d32f2f",
                          fontSize: "13px",
                        }}
                      >
                        Clear Sort
                      </span>
                    </div>
                  </button>
                </>
              )}
            </div>
          </Popover>

          <tbody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row, idx) => {// NOSONAR
                const rowId = isRequest ? row.titleId : row.initiativeId;
                const isChatOpen = activeChatRow === rowId;
                const isExpanded = expandedRow === rowId;
                return (
                  <React.Fragment key={idx + 1}>
                    <tr
                      className={isExpanded ? "row-expanded" : ""}
                      onClick={(e) => {
                        const target = e.target as HTMLElement;

                        if (
                          document
                            .querySelector(".MuiPopover-root")
                            ?.contains(target) ||
                          target.closest(".MuiPopover-root")
                        ) {
                          return;
                        }

                        if (
                          !openMenu &&
                          !target.closest(".chat-icon") &&
                          !target.closest(".info-icon") &&
                          !target.closest("button") &&
                          !target.closest(".chat-popover-container") &&
                          !target.closest(".approver-column") &&
                          !target.closest(".expanded-content")
                        ) {
                          onRowClick?.(row);
                        }
                      }}
                      style={{
                        cursor: onRowClick ? "pointer" : "default",
                        opacity: row.isFaded ? 0.5 : 1,
                      }}
                    >
                      {columnNames.map((header) => {
                        const cellData = row[header.key];
                        const isApproverColumn = header.key === "approver";
                        const isAvatarCell =
                          typeof cellData === "object" &&
                          cellData?.type === "avatar";
                        const isReactCell = React.isValidElement(cellData);
                        let textOverflowValue = "";

                        if (
                          cellData !== null &&
                          cellData !== undefined &&
                          (typeof cellData === "string" ||
                            typeof cellData === "number")
                        ) {
                          textOverflowValue = String(cellData);
                        }

                        return (
                          <td
                            key={header.key}
                            className={
                              isApproverColumn ? "approver-column" : ""
                            }
                          >
                            {isReactCell && cellData}
                            {!isReactCell && isAvatarCell && (
                              <img
                                src={cellData.url}
                                alt="avatar"
                                className="avatar"
                              />
                            )}
                            {!isReactCell && !isAvatarCell && (
                              <TextOverflow value={textOverflowValue} />
                            )}
                          </td>
                        );
                      })}

                      {showChatColumn && (
                        <td>
                          <button
                            style={{
                              border: "none",
                              background: "none",
                              padding: 0,
                              margin: 0,
                              cursor: "pointer",
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (activeChatRow === rowId) {
                                onOpenChat?.(null);
                                setChatAnchorEl(null);
                              } else {
                                setChatAnchorEl(e.currentTarget);
                                onOpenChat?.(rowId);
                              }
                            }}
                          >
                            <img
                              className="chat-icon"
                              src={ChatIcon}
                              alt="Chat"
                              style={{ cursor: "pointer" }}
                            />
                          </button>
                          <Popover
                            open={isChatOpen && !!chatAnchorEl}
                            anchorEl={chatAnchorEl}
                            onClose={() => {
                              onOpenChat?.(null);
                              setChatAnchorEl(null);
                            }}
                            anchorOrigin={{
                              vertical: "bottom",
                              horizontal: "right",
                            }}
                            transformOrigin={{
                              vertical: "top",
                              horizontal: "right",
                            }}
                            slotProps={{
                              paper: {
                                style: {
                                  width: 434,
                                  borderRadius: 16,
                                  overflow: "visible",
                                  marginLeft: 16,
                                  marginTop: 14,
                                },
                              },
                            }}
                          >
                            <div className="chat-popover-container">
                              <ChatPopover
                                comments={commentsByRow?.[rowId] || []}
                                onCommentSubmit={(
                                  html,
                                  parentId,
                                  attachments,
                                ) =>
                                  onCommentSubmit?.(
                                    rowId,
                                    html,
                                    parentId,
                                    attachments,
                                  )
                                }
                                onClose={() => onOpenChat?.(null)}
                                canWrite={canWrite}
                              />
                            </div>
                          </Popover>
                        </td>
                      )}
                      {showInfoColumn && (
                        <td>
                          <Tooltip title={row?.message} arrow placement="top">
                            <img
                              className="info-icon"
                              src={infoIcon}
                              alt="Info"
                              style={{
                                marginTop: "9px",
                                cursor: "pointer",
                                width: "23px",
                                height: "23px",
                              }}
                            />
                          </Tooltip>
                        </td>
                      )}
                      {showActionsColumn && (
                        <td>
                          <img
                            className="actions-icon"
                            src={ActionsIcon}
                            alt="Actions"
                          />
                        </td>
                      )}
                      {showActionsColumnWithOptions && (
                        <td>
                          {isRequest ? (
                            row.rawStatus === "pending" && (
                              <>
                                <IconButton
                                  aria-label="more"
                                  id="long-button"
                                  aria-controls={
                                    openMenu ? "long-menu" : undefined
                                  }
                                  aria-expanded={openMenu ? "true" : undefined}
                                  aria-haspopup="true"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMenuClick(e, row);
                                  }}
                                >
                                  <img src={ActionsIcon} alt="Actions" />
                                </IconButton>

                                <Menu
                                  id="long-menu"
                                  anchorEl={anchorEl}
                                  open={
                                    openMenu && selectedMenuOption === row?.id
                                  }
                                  onClose={handleMenuClose}
                                  slotProps={{
                                    paper: {
                                      style: {
                                        maxHeight: ITEM_HEIGHT * 4.5,
                                        boxShadow:
                                          "0px 4px 16px rgba(0, 0, 0, 0.12)",
                                        borderRadius: 16,
                                      },
                                      sx: {
                                        "&.MuiPaper-root": {
                                          padding: "0px !important",
                                          width: "156px",
                                        },
                                      },
                                    },
                                    list: {
                                      "aria-labelledby": "long-button",
                                    },
                                  }}
                                  anchorOrigin={{
                                    vertical: "bottom",
                                    horizontal: "right",
                                  }}
                                  transformOrigin={{
                                    vertical: "top",
                                    horizontal: "right",
                                  }}
                                >
                                  {menuOptions.map(
                                    (option: any, index: number) => (
                                      <MenuItem
                                        key={
                                          option.key || option.label || index
                                        }
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          option.onClick(
                                            handleMenuClose,
                                            selectedRow,
                                          );
                                        }}
                                        style={option.style}
                                      >
                                        {option.icon &&
                                          (React.isValidElement(option.icon) ? (
                                            option.icon
                                          ) : (
                                            <img
                                              src={option.icon}
                                              alt={
                                                typeof option.label === "string"
                                                  ? option.label
                                                  : "icon"
                                              }
                                            />
                                          ))}
                                        {option.label}
                                      </MenuItem>
                                    ),
                                  )}
                                </Menu>
                              </>
                            )
                          ) : (
                            <>
                              <IconButton
                                aria-label="more"
                                id="long-button"
                                aria-controls={
                                  openMenu ? "long-menu" : undefined
                                }
                                aria-expanded={openMenu ? "true" : undefined}
                                aria-haspopup="true"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleMenuClick(e, row);
                                }}
                              >
                                <img src={ActionsIcon} alt="Actions" />
                              </IconButton>

                              <Menu
                                id="long-menu"
                                anchorEl={anchorEl}
                                open={
                                  openMenu && selectedMenuOption === row?.id
                                }
                                onClose={handleMenuClose}
                                slotProps={{
                                  paper: {
                                    elevation: 1,
                                    style: {
                                      maxHeight: ITEM_HEIGHT * 4.5,
                                      boxShadow:
                                        "0px 4px 16px rgba(0, 0, 0, 0.12)",
                                      borderRadius: 16,
                                    },
                                    sx: {
                                      "&.MuiPaper-root": {
                                        padding: "0px !important",
                                        width: "156px",
                                      },
                                    },
                                  },
                                  list: { "aria-labelledby": "long-button" },
                                }}
                                anchorOrigin={{
                                  vertical: "bottom",
                                  horizontal: "right",
                                }}
                                transformOrigin={{
                                  vertical: "top",
                                  horizontal: "right",
                                }}
                              >
                                {menuOptions.map(
                                  (option: any, index: number) => (
                                    <MenuItem
                                      key={option.key || option.label || index}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        option.onClick(
                                          handleMenuClose,
                                          selectedRow,
                                        );
                                      }}
                                      style={option.style}
                                    >
                                      {option.icon &&
                                        (React.isValidElement(option.icon) ? (
                                          option.icon
                                        ) : (
                                          <img
                                            src={option.icon}
                                            alt={
                                              typeof option.label === "string"
                                                ? option.label
                                                : "icon"
                                            }
                                          />
                                        ))}
                                      {option.label}
                                    </MenuItem>
                                  ),
                                )}
                              </Menu>
                            </>
                          )}
                        </td>
                      )}
                    </tr>

                    {/* Expandable row */}
                    {isExpanded && isRequest && (
                      <tr className="expanded-row">
                        {row.fieldValue ? (
                          <>
                            {/* Columns 0-1: Request Type, Title */}
                            <td style={{ paddingLeft: "13px" }} colSpan={7}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  overflow: "hidden",
                                  paddingTop: "10px",
                                }}
                              >
                                <Tooltip
                                  title={row.fieldValue || ""}
                                  arrow
                                  placement="top"
                                >
                                  <span
                                    style={{
                                      marginLeft: "2px",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    {row.fieldValue}
                                  </span>
                                </Tooltip>
                              </div>
                            </td>

                            {/* Columns 2-4: Linked to, Approver, Status */}

                            {/* Columns 5+: Date, Requester, ... */}

                            {/* Extra columns */}
                            {showChatColumn && <td></td>}
                            {showInfoColumn && <td></td>}
                            {showActionsColumn && <td></td>}
                            {showActionsColumnWithOptions && <td></td>}
                          </>
                        ) : (
                          <td
                            style={{ paddingLeft: "13px" }}
                            colSpan={
                              columnNames.length +
                              (showChatColumn ? 1 : 0) +
                              (showInfoColumn ? 1 : 0) +
                              (showActionsColumn ? 1 : 0) +
                              (showActionsColumnWithOptions ? 1 : 0)
                            }
                          >
                            <Tooltip
                              title={row.message || "-"}
                              arrow
                              placement="top"
                            >
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  display: "inline-block",
                                  paddingTop: "10px",
                                }}
                              >
                                {row.message || "-"}
                              </span>
                            </Tooltip>
                          </td>
                        )}
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={
                    columnNames.length +
                    (showChatColumn ? 1 : 0) +
                    (showInfoColumn ? 1 : 0) +
                    (showActionsColumn ? 1 : 0) +
                    (showActionsColumnWithOptions ? 1 : 0)
                  }
                  style={{ background: "transparent" }}
                >
                  {isShowMessageLoading ? (
                    ""
                  ) : (
                    <div className="no-results-popup">
                      <NoRecordsComponent
                        title={noRecordsTitle}
                        message={noRecordsMessage}
                      />
                    </div>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </OverlayScrollbarsComponent>
    </div>
  );
};

export default MUITableRequest;
